import { html, LitElement } from 'lit';
import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';
import { customElement, property, query } from 'lit/decorators.js';
import { GzFormFieldWebComponent } from '../../../../../../../../ui/gzForm/formField';
import { Category, Level } from '@nirvana/shared/dtos/types/policies/common';
import { FormGroup } from '../../../../../../../../ui/gzForm/formGroup/form-group';
import { styles } from '../view/styles';
import { TextHighlightMixin } from '../../../../../../../../mixins';
import { connect } from 'pwa-helpers';
import { RootState, store } from '@nirvana/redux/store';
import { selectAccordionsSearchState } from '@nirvana/redux/searchable-ui-accordions';
import { columns, rows } from './complex-selection-group-config';
import { GzSelectableTableRowGroupWebComponent } from '../../../../../../../../ui/gzSelectableTableRowGroup';
import { DEFAULT_LEVEL } from '../../../../../utils/uiSettings/sandbox/sandbox.values';
import { GzChipsGeneratorInputWebComponent } from '../../../../../../../../ui/gzChipsGeneratorInput';
import { ComplexSelectionFormValue } from '../../../../../../../../ui/gzSelectableTableRowGroup/types';
import { GzInfoBoxWebComponent } from '../../../../../../../../ui/gzInfoBox';
import { toggleVisibility } from '../../../../../../../../utils/htmlElement.utils';
import { GzInputWebComponent } from '../../../../../../../../ui/gzInput/component/GzInputWebComponent';

@customElement('gz-policy-panel-sandbox-content-prefiltering')
export class GzContentPrefilteringWebComponent extends TextHighlightMixin(connect(store)(LitElement)) {
  @property({ type: Object, attribute: true, reflect: true })
  public contentPrefilteringLabel: { label: string; translatedLabel: string } = {
    label: '',
    translatedLabel: '',
  };

  @property({ type: Object, attribute: true, reflect: true })
  public exceptionsLabel: { label: string; translatedLabel: string } = {
    label: '',
    translatedLabel: '',
  };

  @query('#selection-group-form-field')
  private selectionGroupFormField: GzFormFieldWebComponent<GzSelectableTableRowGroupWebComponent<Category, Level>>;

  @query('#selection-group-warning')
  private selectionGroupWarning: GzInfoBoxWebComponent;

  @query('#extensions-form-field')
  private extensionsFormField: GzFormFieldWebComponent<GzChipsGeneratorInputWebComponent, string[]>;

  public formGroup: FormGroup;

  /**
   * State changed, update search string.
   * @param rootState
   */
  public stateChanged(rootState: RootState) {
    const accordionsSearchApp = selectAccordionsSearchState(rootState);

    this.searchString = accordionsSearchApp.searchString;
  }

  /**
   * Template styles.
   */
  static get styles() {
    return styles;
  }

  firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties);

    this.buildFormGroup();
  }

  protected render() {
    return html`
      <div>
        <gz-title
          searchable-label="${this.contentPrefilteringLabel.label}"
          fontSize="11"
          fontWeight="600"
          class="form-subtitle"
          >${this.contentPrefilteringLabel.translatedLabel}</gz-title
        >

        <gz-text id="content-prefiltering-description-text" fontSize="12" fontWeight="400" class="infoText"
          >${translateFn(
            'This module scans for suspicious files, command-line arguments, and URLs. It then submits objects to Sandbox Analyzer based on the level selected below.'
          )}
        </gz-text>

        <gz-form-field class="label-mw-250" id="selection-group-form-field">
          <gz-selectable-table-row-group
            gz-form-input
            .allowFullRowSelection="${true}"
            .allowFullColumnSelection="${true}"
            .defatultColumn="${DEFAULT_LEVEL}"
            .rows="${rows()}"
            .columns="${columns()}"
            @tableRowGroupSelectionChange="${this.onSelectableTableRowGroupChange}"
          ></gz-selectable-table-row-group>
        </gz-form-field>

        <gz-info-box id="selection-group-warning" severity="warning" class="hidden">
          ${translateFn('Select at least one category.')}
        </gz-info-box>
      </div>

      <div>
        <gz-title searchable-label="${this.exceptionsLabel.label}" fontSize="11" fontWeight="600" class="form-subtitle"
          >${this.exceptionsLabel.translatedLabel}</gz-title
        >

        <gz-text id="exclusions-description-text" fontSize="12" fontWeight="400" class="infoText"
          >${translateFn('Add exclusions:')}
        </gz-text>

        <gz-form-field class="label-mw-250" id="extensions-form-field">
          <gz-chips-generator-input
            gz-form-input
            .hasFloatingLabel="${true}"
            .strings="${{
              label: this.exceptionsLabel.translatedLabel,
              placeholder: translateFn('Specify extensions here. Separate them by comma or press Enter.'),
              notUniqueErrorMessage: translateFn('The entry already exists.'),
              hintText: translateFn('Files with the specified extensions will not be submitted to Sandbox Analyzer.'),
              clearListButtonText: translateFn('Clear list'),
              searchPlaceholder: translateFn('Search'),
              noSearchResultsString: translateFn('No results found'),
              deleteWindowTitle: translateFn('Delete extensions?'),
              deleteWindowContentText: translateFn(
                'You are about to delete the extensions displayed in the list. This action cannot be undone.'
              ),
              cancelClearListBtnString: translateFn('Cancel'),
              clearListActionBtnString: translateFn('Delete'),
            }}"
          >
          </gz-chips-generator-input>
        </gz-form-field>
      </div>
    `;
  }

  private onSelectableTableRowGroupChange(event: CustomEvent) {
    const categories: ComplexSelectionFormValue<Category, Level> = event.detail;
    let hasSelection = false;
    Object.entries(categories).forEach(([_, value]) => {
      if (value.enabled) {
        hasSelection = true;
        return;
      }
    });

    // show warning if no selection
    toggleVisibility(this.selectionGroupWarning, !hasSelection);
  }

  private buildFormGroup() {
    this.formGroup = FormGroup.build({
      categories: this.selectionGroupFormField,
      excludeExtensions: this.extensionsFormField,
    });
  }

  public reset() {
    // reset input of chips generator
    const chipsGeneratorFormField = this.extensionsFormField.Input.shadowRoot?.querySelector(
      '#chips-generator-form-field'
    ) as GzFormFieldWebComponent<GzInputWebComponent, string>;
    chipsGeneratorFormField?.Input?.setFormValue('');
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-policy-panel-sandbox-content-prefiltering': GzContentPrefilteringWebComponent;
  }
}
